import { MONTHS, DAYS_SHORT } from '../utils/constants.js';
import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { api, publicApi } from '../utils/api.js';
import { toast } from '../utils/toast.js';

function IcoBell() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  );
}

function NotifRow({ notif, isNew, onDismiss, onClick }) {
  const [hovered, setHovered] = useState(false);
  const [dismissHovered, setDismissHovered] = useState(false);

  const timeAgo = (date) => {
    if (!date) return '—';
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div style={{
      position: 'relative',
      borderBottom: '1px solid rgba(255,255,255,0.04)',
      background: hovered ? 'rgba(255,255,255,0.025)' : 'transparent',
      transition: 'background .12s',
    }}>
      {isNew && (
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: 2, background: 'linear-gradient(180deg, #D4A843, #c49535)',
          borderRadius: '0 1px 1px 0',
        }} />
      )}
      <button
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          width: '100%', textAlign: 'left', background: 'none', border: 'none',
          cursor: 'pointer', padding: '10px 40px 10px 18px',
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}
      >
        <div style={{
          width: 30, height: 30, borderRadius: 8, flexShrink: 0,
          background: isNew ? 'rgba(212,168,67,0.12)' : 'rgba(255,255,255,0.04)',
          border: `1px solid ${isNew ? 'rgba(212,168,67,0.25)' : 'rgba(255,255,255,0.07)'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={isNew ? '#D4A843' : 'rgba(232,228,220,0.35)'} strokeWidth="1.8">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
        <div style={{ minWidth: 0, flex: 1, paddingTop: 1 }}>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: isNew ? '#EAE6DC' : 'rgba(232,228,220,0.5)' }}>
            <span style={{ fontWeight: 600, color: isNew ? '#D4A843' : 'rgba(212,168,67,0.6)' }}>
              {notif.refereeName || 'Someone'}
            </span>
            <span style={{ fontWeight: 400 }}> joined via your referral link</span>
            {notif.planName && (
              <span style={{
                marginLeft: 6, fontSize: 10, fontWeight: 600,
                color: 'rgba(212,168,67,0.6)', background: 'rgba(212,168,67,0.08)',
                border: '1px solid rgba(212,168,67,0.15)', borderRadius: 4,
                padding: '1px 5px', letterSpacing: '.02em',
              }}>
                {notif.planName}
              </span>
            )}
          </div>
          <div style={{ fontSize: 10.5, color: 'rgba(232,228,220,0.25)', marginTop: 3, letterSpacing: '.01em' }}>
            {timeAgo(notif.joinedAt)}
          </div>
        </div>
      </button>
      <button
        onClick={e => { e.stopPropagation(); onDismiss(notif.id); }}
        onMouseEnter={() => setDismissHovered(true)}
        onMouseLeave={() => setDismissHovered(false)}
        title="Dismiss"
        style={{
          position: 'absolute', top: '50%', right: 10,
          transform: 'translateY(-50%)',
          width: 20, height: 20,
          background: dismissHovered ? 'rgba(255,255,255,0.07)' : 'none',
          border: 'none', cursor: 'pointer',
          color: dismissHovered ? 'rgba(232,228,220,0.6)' : 'rgba(232,228,220,0.18)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 5, transition: 'all .12s', flexShrink: 0,
        }}
      >
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  );
}


function AnnouncementNotifRow({ ann, onDismiss }) {
  const TYPE_CONFIG = {
    info:     { color: '#60a5fa', icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg> },
    success:  { color: '#34d399', icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> },
    warning:  { color: '#fbbf24', icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> },
    critical: { color: '#f87171', icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg> },
  };
  const cfg   = TYPE_CONFIG[ann.type] || TYPE_CONFIG.info;
  const color = cfg.color;
  const LIMIT = 120;
  const isLong = (ann.body || '').length > LIMIT;
  const [expanded, setExpanded] = useState(false);
  const [dismissHovered, setDismissHovered] = useState(false);

  const displayBody = isLong && !expanded
    ? ann.body.slice(0, LIMIT).trimEnd() + '…'
    : ann.body;

  return (
    <div style={{
      position: 'relative',
      borderBottom: '1px solid rgba(255,255,255,0.04)',
    }}>
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0,
        width: 2, background: color, opacity: 0.5, borderRadius: '0 1px 1px 0',
      }} />
      <div style={{ padding: '10px 40px 10px 18px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{
          width: 26, height: 26, borderRadius: 7, flexShrink: 0,
          background: `${color}14`, border: `1px solid ${color}28`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', color,
        }}>
          {cfg.icon}
        </div>
        <div style={{ minWidth: 0, flex: 1, paddingTop: 1 }}>
          {ann.title && (
            <div style={{ fontSize: 12, fontWeight: 600, color: '#EAE6DC', lineHeight: 1.4, marginBottom: 3 }}>
              {ann.title}
            </div>
          )}
          {ann.body && (
            <div style={{ fontSize: 11.5, color: 'rgba(232,228,220,0.5)', lineHeight: 1.55 }}>
              {displayBody}
              {isLong && (
                <button
                  onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color, fontSize: 11, fontWeight: 600, padding: '0 0 0 4px',
                    letterSpacing: '.01em',
                  }}
                >
                  {expanded ? 'less ↑' : 'more ↓'}
                </button>
              )}
            </div>
          )}
          <div style={{ fontSize: 10.5, color: 'rgba(232,228,220,0.22)', marginTop: 4, letterSpacing: '.01em' }}>
            {new Date(ann.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        </div>
      </div>
      <button
        onClick={() => onDismiss(ann.id)}
        onMouseEnter={() => setDismissHovered(true)}
        onMouseLeave={() => setDismissHovered(false)}
        title="Dismiss"
        style={{
          position: 'absolute', top: 10, right: 10,
          width: 20, height: 20,
          background: dismissHovered ? 'rgba(255,255,255,0.07)' : 'none',
          border: 'none', cursor: 'pointer',
          color: dismissHovered ? 'rgba(232,228,220,0.6)' : 'rgba(232,228,220,0.18)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 5, transition: 'all .12s',
        }}
      >
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  );
}

// ── Notification bell + portal dropdown ──────────────────────────────────────
function NotificationBell({ userId, onNavigateReferral }) {
  const [open,          setOpen]          = useState(false);
  const [referrals,     setReferrals]     = useState([]); // latest 5 non-dismissed (for dropdown)
  const [totalUnseen,   setTotalUnseen]   = useState(0);  // full unread count across ALL referrals (for badge)
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedAnns, setDismissedAnns] = useState([]);

  // Load per-user dismissed list once userId is known
  useEffect(() => {
    if (!userId) return;
    try {
      const stored = JSON.parse(localStorage.getItem(`kfx_dismissed_ann_${userId}`) || '[]');
      setDismissedAnns(stored);
    } catch { setDismissedAnns([]); }
  }, [userId]);
  const [dropPos,       setDropPos]       = useState({ top: 0, right: 0 });

  const bellRef    = useRef(null);
  const dropRef    = useRef(null);
  const prevIdsRef = useRef(null);
  const POLL_MS    = 60_000;

  // Backend returns:
  //   referrals   — latest 5 non-dismissed (bell dropdown list)
  //   totalUnseen — full count of ALL unread referrals (badge number)
  const fetchReferrals = useCallback(async () => {
    try {
      const d        = await api('/referral/dashboard');
      const incoming = d?.referrals   || [];
      const unseen   = d?.totalUnseen ?? 0;

      // Toast only for new entries appearing in the top-5 list
      if (prevIdsRef.current !== null) {
        incoming
          .filter(r => !prevIdsRef.current.has(r.id))
          .forEach(r => {
            toast(
              `🎉 <strong>${r.refereeName || 'Someone'}</strong> joined using your referral link!`,
              'referral', 5000
            );
          });
      }
      prevIdsRef.current = new Set(incoming.map(r => r.id));
      setReferrals(incoming);
      setTotalUnseen(unseen);
    } catch {}
  }, []);

  useEffect(() => {
    if (!userId) return;
    fetchReferrals();
    publicApi.getActiveAnnouncements().then(d => setAnnouncements(d.announcements || [])).catch(() => {});
    const timer = setInterval(fetchReferrals, POLL_MS);
    return () => clearInterval(timer);
  }, [fetchReferrals, userId]);

  const openDropdown = () => {
    if (bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 8 + window.scrollY, right: window.innerWidth - rect.right });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        dropRef.current && !dropRef.current.contains(e.target) &&
        bellRef.current && !bellRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const dismissAnn = (id) => {
    const next = [...dismissedAnns, id];
    setDismissedAnns(next);
    try { localStorage.setItem(`kfx_dismissed_ann_${userId}`, JSON.stringify(next)); } catch {}
  };

  const visibleAnns = announcements.filter(a => !dismissedAnns.includes(a.id));

  // Badge shows full unread count (not just the 5 in the dropdown)
  const unreadCount = totalUnseen + visibleAnns.length;

  // Mark all visible as read — also clears them from the dropdown so the bell goes quiet
  const markAllRead = useCallback(async () => {
    const allIds = referrals.map(r => r.id);
    if (!allIds.length) return;

    // Optimistically clear badge and hide items from dropdown
    setTotalUnseen(0);
    setReferrals([]);

    try {
      // Must call /dismiss (sets dismissedAt) — not /mark-read (only sets seen_at).
      // The bell filter on the backend excludes rows by dismissedAt, not seen_at.
      // If we only mark-read, notifications come back on next fetch/refresh.
      await api('/referral/notifications/dismiss', {
        method: 'POST',
        body: JSON.stringify({ ids: allIds }),
      });
    } catch {
      fetchReferrals();
    }
  }, [referrals, fetchReferrals]);

  // Dismiss one from the dropdown — remove it immediately, no instant re-fetch
  const dismissOne = useCallback(async (id) => {
    const wasUnread = referrals.find(r => r.id === id && !r.seen_at);
    setReferrals(prev => prev.filter(r => r.id !== id));
    if (wasUnread) setTotalUnseen(prev => Math.max(0, prev - 1));

    try {
      await api('/referral/notifications/dismiss', {
        method: 'POST',
        body: JSON.stringify({ ids: [id] }),
      });
      // Intentionally no fetchReferrals() here — the poll interval will sync naturally.
      // Calling it immediately races with the DB write and re-populates the dismissed item.
    } catch {
      fetchReferrals();
    }
  }, [referrals, fetchReferrals]);

  // Dismiss (clear) all visible — wipes badge and dropdown immediately
  const dismissAll = useCallback(async () => {
    const allIds = referrals.map(r => r.id);
    if (!allIds.length) return;

    // Optimistically wipe everything right away
    setReferrals([]);
    setTotalUnseen(0);
    setOpen(false);

    try {
      await api('/referral/notifications/dismiss', {
        method: 'POST',
        body: JSON.stringify({ ids: allIds }),
      });
      // No immediate re-fetch — avoids race condition where backend hasn't committed yet
    } catch {
      fetchReferrals();
    }
  }, [referrals, fetchReferrals]);

  const handleNotifClick = useCallback(async (notif) => {
    setReferrals(prev => prev.filter(r => r.id !== notif.id));
    if (!notif.seen_at) setTotalUnseen(prev => Math.max(0, prev - 1));
    setOpen(false);
    onNavigateReferral();

    try {
      await api('/referral/notifications/dismiss', {
        method: 'POST',
        body: JSON.stringify({ ids: [notif.id] }),
      });
    } catch {}
  }, [onNavigateReferral]);

  return (
    <>
      <button
        ref={bellRef}
        onClick={() => open ? setOpen(false) : openDropdown()}
        aria-label="Notifications"
        style={{
          position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 34, height: 34, borderRadius: 8,
          background: open ? 'rgba(212,168,67,0.12)' : 'rgba(255,255,255,0.05)',
          border: open ? '1px solid rgba(212,168,67,0.3)' : '1px solid rgba(255,255,255,0.08)',
          color: open ? '#D4A843' : 'rgba(232,228,220,0.55)',
          cursor: 'pointer', transition: 'all .16s', flexShrink: 0,
        }}
        onMouseEnter={e => { if (!open) { e.currentTarget.style.background = 'rgba(212,168,67,0.07)'; e.currentTarget.style.color = 'rgba(232,228,220,0.85)'; } }}
        onMouseLeave={e => { if (!open) { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'rgba(232,228,220,0.55)'; } }}
      >
        <IcoBell />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            minWidth: 16, height: 16, borderRadius: 8,
            background: '#D4A843', color: '#0b0d11',
            fontSize: 9, fontWeight: 800, fontFamily: "'Inter',sans-serif",
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px',
            boxShadow: '0 0 8px rgba(212,168,67,.55)',
            animation: 'badgePop .3s cubic-bezier(.4,0,.2,1) both',
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={dropRef}
          style={{
            position: 'absolute',
            top:   dropPos.top,
            right: dropPos.right,
            width: 320,
            background: '#0e0f12',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 12,
            boxShadow: '0 0 0 1px rgba(0,0,0,0.5), 0 8px 16px rgba(0,0,0,0.4), 0 24px 48px rgba(0,0,0,0.35)',
            zIndex: 999999,
            overflow: 'hidden',
            animation: 'dropIn .16s cubic-bezier(.22,1,.36,1) both',
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 14px 11px',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(232,228,220,0.9)', letterSpacing: '.01em' }}>
                Notifications
              </span>
              {unreadCount > 0 && (
                <span style={{
                  background: '#D4A843', color: '#0b0d11',
                  fontSize: 9.5, fontWeight: 700, padding: '1.5px 6px', borderRadius: 20,
                  letterSpacing: '.03em', lineHeight: 1.5,
                }}>
                  {unreadCount}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 2 }}>
              {referrals.some(r => !r.seen_at) && (
                <button onClick={markAllRead} style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 11, color: 'rgba(232,228,220,0.35)',
                  padding: '3px 7px', borderRadius: 6, transition: 'color .12s, background .12s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.color = 'rgba(232,228,220,0.75)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'rgba(232,228,220,0.35)'; e.currentTarget.style.background = 'none'; }}
                >
                  Mark read
                </button>
              )}
              {referrals.length > 0 && (
                <button onClick={dismissAll} style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 11, color: 'rgba(232,228,220,0.35)',
                  padding: '3px 7px', borderRadius: 6, transition: 'color .12s, background .12s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.color = 'rgba(232,228,220,0.75)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'rgba(232,228,220,0.35)'; e.currentTarget.style.background = 'none'; }}
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Scrollable body */}
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>

            {/* Announcements */}
            {visibleAnns.length > 0 && (
              <div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px 6px',
                }}>
                  <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: 'rgba(232,228,220,0.25)' }}>
                    Announcements
                  </span>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.05)' }} />
                </div>
                {visibleAnns.map(a => (
                  <AnnouncementNotifRow key={a.id} ann={a} onDismiss={dismissAnn}/>
                ))}
              </div>
            )}

            {/* Referral notifications or empty state */}
            {referrals.length > 0 && visibleAnns.length > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 14px 6px',
              }}>
                <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: 'rgba(232,228,220,0.25)' }}>
                  Referrals
                </span>
                <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.05)' }} />
              </div>
            )}

            {referrals.length === 0 && visibleAnns.length === 0 ? (
              <div style={{ padding: '36px 20px 32px', textAlign: 'center' }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 12, margin: '0 auto 12px',
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(232,228,220,0.25)" strokeWidth="1.6">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                </div>
                <div style={{ fontSize: 12.5, fontWeight: 500, color: 'rgba(232,228,220,0.5)', marginBottom: 5 }}>
                  You're all caught up
                </div>
                <div style={{ fontSize: 11.5, color: 'rgba(232,228,220,0.25)', lineHeight: 1.55 }}>
                  Share your referral link to<br/>start earning rewards.
                </div>
              </div>
            ) : (
              referrals.map(r => (
                <NotifRow
                  key={r.id}
                  notif={r}
                  isNew={!r.seen_at}
                  onDismiss={dismissOne}
                  onClick={() => handleNotifClick(r)}
                />
              ))
            )}
          </div>

          {/* Footer */}
          {referrals.length > 0 && (
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '8px 10px' }}>
              <button
                onClick={() => { markAllRead(); setOpen(false); onNavigateReferral(); }}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8, padding: '7px 12px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  color: 'rgba(232,228,220,0.4)', fontSize: 11.5, fontWeight: 500,
                  cursor: 'pointer', transition: 'all .12s', letterSpacing: '.01em',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.13)';
                  e.currentTarget.style.color = 'rgba(232,228,220,0.7)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                  e.currentTarget.style.color = 'rgba(232,228,220,0.4)';
                }}
              >
                <span>View Referral Dashboard</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {unreadCount > 0 && (
                    <span style={{ fontSize: 10, color: '#D4A843', fontWeight: 600 }}>
                      {unreadCount} unread
                    </span>
                  )}
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                  </svg>
                </span>
              </button>
            </div>
          )}
        </div>,
        document.body
      )}

      <style>{`
        @keyframes dropIn {
          from { opacity:0; transform:translateY(-4px) scale(.98); }
          to   { opacity:1; transform:translateY(0) scale(1); }
        }
        @keyframes badgePop {
          from { transform:scale(0); opacity:0; }
          to   { transform:scale(1); opacity:1; }
        }
      `}</style>
    </>
  );
}

// ── Account type meta ─────────────────────────────────────────────────────────
const ACCT_META = {
  Live:        { color: '#2ECC8A', bg: 'rgba(46,204,138,.12)',  border: 'rgba(46,204,138,.25)',  icon: '📈' },
  Demo:        { color: '#5B8FE8', bg: 'rgba(91,143,232,.12)',  border: 'rgba(91,143,232,.25)',  icon: '🧪' },
  'Prop Firm': { color: '#D4A843', bg: 'rgba(212,168,67,.12)',  border: 'rgba(212,168,67,.25)',  icon: '🏆' },
  Paper:       { color: 'rgba(200,192,168,.6)', bg: 'rgba(200,192,168,.07)', border: 'rgba(200,192,168,.18)', icon: '📄' },
};

function TopbarAccountSwitcher({ tradingAccounts = [], activeAccountId, onSetActive, onManage }) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);
  const dropRef = useRef(null);

  const activeAcct = tradingAccounts.find(a => a.id === activeAccountId);
  const meta = ACCT_META[activeAcct?.type] || null;

  const openDrop = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 8 + window.scrollY, right: window.innerWidth - r.right });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (dropRef.current && !dropRef.current.contains(e.target) &&
          btnRef.current  && !btnRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => open ? setOpen(false) : openDrop()}
        style={{
          display: 'flex', alignItems: 'center', gap: 7,
          padding: '0 10px', height: 34, borderRadius: 8,
          background: open ? 'rgba(212,168,67,.1)' : 'rgba(255,255,255,.05)',
          border: open ? '1px solid rgba(212,168,67,.3)' : '1px solid rgba(255,255,255,.08)',
          color: activeAcct ? 'rgba(240,234,214,.85)' : 'rgba(200,192,168,.4)',
          cursor: 'pointer', transition: 'all .15s', flexShrink: 0,
          fontFamily: "'Inter', sans-serif",
        }}
        onMouseEnter={e => { if (!open) { e.currentTarget.style.background = 'rgba(212,168,67,.07)'; e.currentTarget.style.borderColor = 'rgba(212,168,67,.2)'; }}}
        onMouseLeave={e => { if (!open) { e.currentTarget.style.background = 'rgba(255,255,255,.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,.08)'; }}}
      >
        {/* Type dot */}
        {activeAcct && meta ? (
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta.color, boxShadow: `0 0 5px ${meta.color}`, flexShrink: 0 }} />
        ) : (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ opacity: .4 }}>
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          </svg>
        )}

        <span style={{ fontSize: 12, fontWeight: 600, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {activeAcct ? activeAcct.name : 'No account'}
        </span>

        {activeAcct && (
          <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: '.06em', color: meta?.color, background: meta?.bg, border: `1px solid ${meta?.border}`, padding: '1px 5px', borderRadius: 3, flexShrink: 0, fontFamily: "'DM Sans',sans-serif" }}>
            {activeAcct.type}
          </span>
        )}

        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          style={{ flexShrink: 0, opacity: .4, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && createPortal(
        <div
          ref={dropRef}
          style={{
            position: 'absolute', top: dropPos.top, right: dropPos.right,
            width: 260, zIndex: 999999,
            background: '#111310',
            border: '1px solid rgba(255,255,255,.1)',
            borderRadius: 12,
            boxShadow: '0 20px 60px rgba(0,0,0,.7), 0 0 0 1px rgba(212,168,67,.06)',
            overflow: 'hidden',
            animation: 'dropIn .18s cubic-bezier(.4,0,.2,1) both',
          }}
        >
          <div style={{ height: 2, background: 'linear-gradient(90deg, transparent, rgba(212,168,67,.6), transparent)' }} />

          {/* Account list */}
          {tradingAccounts.length > 0 ? (
            <div style={{ padding: '6px 0' }}>
              {tradingAccounts.map(acct => {
                const m = ACCT_META[acct.type] || ACCT_META['Live'];
                const isActive = acct.id === activeAccountId;
                return (
                  <button key={acct.id} onClick={() => { onSetActive(acct.id); setOpen(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '9px 14px',
                      background: isActive ? 'rgba(212,168,67,.07)' : 'transparent',
                      border: 'none', cursor: 'pointer', textAlign: 'left',
                      transition: 'background .12s',
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,.04)'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ width: 30, height: 30, flexShrink: 0, background: m.bg, border: `1px solid ${m.border}`, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                      {m.icon}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: isActive ? '#D4A843' : 'rgba(240,234,214,.8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Inter',sans-serif" }}>
                          {acct.name}
                        </span>
                        <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: '.06em', color: m.color, background: m.bg, border: `1px solid ${m.border}`, padding: '1px 5px', borderRadius: 3, flexShrink: 0, fontFamily: "'DM Sans',sans-serif" }}>
                          {acct.type}
                        </span>
                      </div>
                      {acct.balance && (
                        <div style={{ fontSize: 10, color: 'rgba(200,192,168,.4)', marginTop: 1, fontFamily: "'Inter',sans-serif" }}>
                          ${parseFloat(acct.balance).toLocaleString()}{acct.broker ? ` · ${acct.broker}` : ''}
                        </div>
                      )}
                    </span>
                    {isActive && (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#D4A843" strokeWidth="2.5" style={{ flexShrink: 0 }}>
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: '20px 16px', textAlign: 'center', color: 'rgba(200,192,168,.4)', fontSize: 12, fontFamily: "'Inter',sans-serif", lineHeight: 1.6 }}>
              No accounts yet.<br/>Add one in Account Settings.
            </div>
          )}

          {/* Footer */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,.06)', padding: 8 }}>
            <button onClick={() => { onManage(); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%', padding: '8px 10px',
                background: 'transparent', border: '1px solid rgba(255,255,255,.07)',
                color: 'rgba(200,192,168,.5)', fontSize: 11, fontWeight: 500,
                cursor: 'pointer', borderRadius: 7, fontFamily: "'DM Sans',sans-serif",
                transition: 'all .12s',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = 'rgba(240,234,214,.75)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,.14)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(200,192,168,.5)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,.07)'; }}
            >
              <span>Manage Accounts</span>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// ── Topbar ────────────────────────────────────────────────────────────────────
export default function Topbar({ page, onNewTrade, onHamburger, setPage, session, tradingAccounts, activeAccountId, onSetActiveAccount }) {
  const now = new Date();
  const dateStr = `${DAYS_SHORT[now.getDay()]} ${now.getDate()} ${MONTHS[now.getMonth()]} ${now.getFullYear()}`;

  const PAGE_META = {
    d:        { title: 'Dashboard',      crumb: 'Dashboard'     },
    analytics:{ title: 'Analytics',      crumb: 'Analytics'     },
    j:        { title: 'Trade Journal',  crumb: 'Trade Journal' },
    nb:       { title: 'Notebook',       crumb: 'Notebook'      },
    ai:       { title: 'AI Coach',       crumb: 'AI Coach'      },
    sim:      { title: 'What-If Sim',    crumb: 'What-If Sim'   },
    chart:    { title: 'Chart Analysis', crumb: 'Chart'         },
    settings: { title: 'Settings',       crumb: 'Settings'      },
    referral: { title: 'Refer & Earn',   crumb: 'Refer & Earn'  },
    tools:    { title: 'Trading Tools',  crumb: 'Tools'         },
  };

  const m = PAGE_META[page] || PAGE_META.d;

  return (
    <div className="topbar">
      <div className="topbar-left">
        <button className="hamburger" onClick={onHamburger} aria-label="Menu">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6"  x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <div>
          <div className="topbar-breadcrumb">KFX Journal / <span>{m.crumb}</span></div>
          <div className="topbar-title">{m.title}</div>
        </div>
      </div>

      <div className="topbar-right">
        <TopbarAccountSwitcher
          tradingAccounts={tradingAccounts || []}
          activeAccountId={activeAccountId}
          onSetActive={id => onSetActiveAccount?.(id)}
          onManage={() => setPage?.('accounts')}
        />
        <div className="topbar-date topbar-datetime">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
            style={{ marginRight: 4, opacity: .5, verticalAlign: 'middle' }}>
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8"  y1="2" x2="8"  y2="6"/>
            <line x1="3"  y1="10" x2="21" y2="10"/>
          </svg>
          {dateStr}
        </div>
        <NotificationBell
          userId={session?.id}
          onNavigateReferral={() => setPage?.('referral')}
        />
        <button className="btn-gold topbar-log" onClick={onNewTrade}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="12" y1="5"  x2="12" y2="19"/>
            <line x1="5"  y1="12" x2="19" y2="12"/>
          </svg>
          LOG TRADE
        </button>
      </div>
    </div>
  );
}